import { Link } from 'react-router-dom';
import { useSurveyI18n } from '../i18n';
import type { SurveyPayload } from '../report';
import { seasonsSpan, type Scope, type Season } from '../seasons';
import { fmtDay } from '../utils';
import { Brand } from './Brand';
import { LangToggle } from './LangToggle';
import type { SwitchOption } from '@/features/dashboard/config';
import { ReportOptions } from '@/features/dashboard/components/ReportOptions';

type Props = {
  campaign: SurveyPayload['campaign'];
  isPreview: boolean;
  /** "21:44:48", or null when the report has never been refreshed. */
  updatedAt: string | null;
  seasons: Season[];
  /** Both seasons ('all') or one season's id. */
  scope: Scope;
  onScope: (scope: Scope) => void;
  onExport: () => void;
  switchOptions: SwitchOption[];
  onSwitch: (path: string) => void;
  /** Null hides the "Winners & prizes" link. */
  winnersPath: string | null;
  isAdmin: boolean;
  onManageUsers: () => void;
  onSignOut: () => void;
};

/** Blue band from the design: brand, live state, period, export, report switcher; language and account actions below. */
export function Header(props: Props) {
  const { t } = useSurveyI18n();
  const { campaign, seasons, scope } = props;
  const day = (iso: string) => fmtDay(iso, t.locale, 'dayMonth');
  const span = seasonsSpan(seasons, campaign);
  // "Both seasons · 6 Oct → 13 Dec", then each season by its own weeks.
  const options = [
    ...(seasons.length > 1 ? [{ value: 'all', label: t.scopeAll(seasons.length, day(span.start), day(span.end)) }] : []),
    ...seasons.map((s) => ({ value: s.id, label: t.scopeSeason(s.n, 1, s.weeks.length) })),
  ];
  const selected = options.find((o) => o.value === scope) ?? options[0];

  return (
    <header className="su-header">
      <div className="su-wrap su-header-inner">
        <div className="su-header-top">
          <Brand title={campaign.name} subtitle={seasons.length > 1 ? `${t.subtitle} · ${seasons.map((s) => t.season(s.n)).join(' + ')}` : t.subtitle} />
          <div className="su-controls">
            {props.isPreview && <span className="su-preview-pill">{t.noLiveData}</span>}
            <div className="su-live">
              <span className={`su-live-dot${campaign.live ? ' on' : ''}`} />
              <strong>{campaign.live ? t.live : t.notLive}</strong>
              {props.updatedAt && <span className="su-live-sub">{t.updatedAt(props.updatedAt)}</span>}
            </div>
            {selected && (
              <div className="su-period">
                <span className="su-period-label">{t.period}</span>
                {options.length > 1 ? (
                  <select aria-label={t.period} value={selected.value} onChange={(e) => props.onScope(e.target.value)}>
                    {options.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span className="su-period-static">{selected.label}</span>
                )}
              </div>
            )}
            <button type="button" className="su-export" onClick={props.onExport}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v12" />
                <path d="M7 10l5 5 5-5" />
                <path d="M4 20h16" />
              </svg>
              {t.exportLabel}
            </button>
            {/* `switchOptions` already excludes this report, so one option is a real destination. */}
            {props.switchOptions.length > 0 && (
              <select className="su-switch" value="" aria-label={t.report} onChange={(e) => e.target.value && props.onSwitch(e.target.value)}>
                <option value="">{t.reportPlaceholder}</option>
                <ReportOptions options={props.switchOptions} labels={{ active: t.reportsActive, archived: t.reportsArchived }} />
              </select>
            )}
          </div>
        </div>
        <div className="su-header-bottom">
          <div className="su-header-actions">
            <LangToggle />
            {props.isAdmin && (
              <button type="button" className="su-ghost small" onClick={props.onManageUsers}>
                {t.users}
              </button>
            )}
            <button type="button" className="su-ghost small" onClick={props.onSignOut}>
              {t.signOut}
            </button>
            {props.winnersPath && (
              <Link className="su-ghost" to={props.winnersPath}>
                {t.winnersLink}
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
